import { useEffect } from "react"

import { CloseOutlined } from "@ant-design/icons"
import { App, Modal, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import AddCommentRequestForm from "../../../../Components/Forms/AddCommentRequestForm"
import Creators from "../../reducer"
import { useRequestsPageQuery } from "../../useRequestsPageQuery"

const { Text } = Typography

export const AddCommentRequestModal = ({ requestType }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const { addCommentRequestID } = useRequestsPageQuery(search)

  const {
    selectedRequest,
    updateFetching,
    updateResult,
    saveFetching,
    saveResult,
    deleteFetching,
    deleteResult
  } = useSelector((state) => state.requests)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const open = !!addCommentRequestID && !!selectedRequest

  const isEdit = !!selectedRequest?.admin_screenshots?.length || !!selectedRequest?.admin_comment

  const isFetching = saveFetching || updateFetching || deleteFetching

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const handleCloseModal = () => {
    const searchParams = new URLSearchParams(search)
    searchParams.delete("add_comment")
    navigate({ pathname, search: `?${searchParams.toString()}` }, { replace: true })
    dispatch(Creators.requestsSetProp("selectedRequest", null))
    if (saveResult) dispatch(Creators.requestsSetProp("saveResult", null))
    if (updateResult) dispatch(Creators.requestsSetProp("updateResult", null))
    if (deleteResult) dispatch(Creators.requestsSetProp("deleteResult", null))
  }

  useEffect(() => {
    if (isFetching) return
    if (saveResult || updateResult || deleteResult) {
      let message = null
      if (isEdit) {
        message = "Your comment has been updated."
      } else {
        message = "You comments has been added to the request."
      }
      notification.success({ message, ...notificationConfig })
      handleCloseModal()
    }
  }, [saveResult, updateResult, deleteResult, isFetching, isEdit])

  const deleteScreenshots = (params) => {
    return new Promise((resolve, reject) => {
      dispatch(
        Creators.deleteScreenshotsRequestRequest({
          ...params,
          onSuccess: resolve,
          onError: reject
        })
      )
    })
  }

  const handleSubmitForm = async (data) => {
    const defaultData = {
      id: selectedRequest?.id || addCommentRequestID,
      requestType
    }

    try {
      if (isEdit && data?.delete_files?.length) {
        await deleteScreenshots({ ...defaultData, delete_files: data?.delete_files })
      }
      if (data?.file_list?.length)
        dispatch(
          Creators.saveScreenshotsRequestRequest({ ...defaultData, file_list: data?.file_list })
        )
      if (selectedRequest?.admin_comment !== data?.admin_comment)
        dispatch(
          Creators.updateCommentRequestRequest({
            ...defaultData,
            admin_comment: data?.admin_comment
          })
        )
    } catch (error) {
      console.error("Request failed:", error)
    }
  }

  return (
    <Modal
      open={open}
      centered={!isMobile}
      width={442}
      title={isEdit ? "Edit comments" : "Add comments"}
      data-testid="deflect-admin-comment-modal"
      closeIcon={<CloseOutlined data-testid="deflect-admin-modal-close" />}
      footer={null}
      closable="true"
      onCancel={handleCloseModal}
      destroyOnClose
      classNames={{ content: "clear-bg" }}
      getContainer={() => document.getElementById("global-wrap")}
    >
      <Spin size="large" spinning={isFetching}>
        <AddCommentRequestForm
          isEdit={isEdit}
          initialValues={selectedRequest}
          onSubmit={handleSubmitForm}
          onClose={handleCloseModal}
        />
      </Spin>
    </Modal>
  )
}
